import { FunctionComponent, useState, useEffect } from "react";
import { Text } from "components/text";
import { useFavorites } from "hooks/useFavorites";
import { Star, ExpandMore, ChevronRight } from "@mui/icons-material";
import { NavigationLink } from "components/navigation";
import styles from "./FavoritesPanel.module.css";

export const FavoritesPanel: FunctionComponent = () => {
  const { favorites, toggleFavorite } = useFavorites();
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});

  // Group favorites by project
  const groupedFavorites = favorites.reduce((acc, favorite) => {
    const projectName = favorite?.projectName || "Unknown Project";
    if (!acc[projectName]) {
      acc[projectName] = [];
    }
    acc[projectName].push(favorite);
    return acc;
  }, {} as Record<string, typeof favorites>);

  // Initialize new projects as expanded when favorites change
  useEffect(() => {
    setExpandedProjects(prev => {
      const updated = { ...prev };
      Object.keys(groupedFavorites).forEach(projectName => {
        if (!(projectName in updated)) {
          updated[projectName] = true; // Default new projects to expanded
        }
      });
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favorites]);

  if (favorites.length === 0) {
    return (
      <div className={styles.favoritesPanel}>
        <Text size="medium">Favorites</Text>
        <Text size="small" color="muted">
          No favorites yet. Star a form schema to add it here for quick access.
        </Text>
      </div>
    );
  }

  const toggleProject = (projectName: string) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectName]: !prev[projectName]
    }));
  };

  return (
    <div className={styles.favoritesPanel}>
      <Text size="medium">Favorites</Text>
      {Object.entries(groupedFavorites).map(([projectName, projectFavorites]) => {
        const isExpanded = expandedProjects[projectName];
        return (
          <div key={projectName} className={styles.projectGroup}>
            <div
              className={styles.projectHeader}
              onClick={() => toggleProject(projectName)}
            >
              {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              <Text size="medium">
                {projectName}
              </Text>
            </div>
            {isExpanded && (
              <div className={styles.favoritesList}>
            {projectFavorites.map((favorite, index) => {
              // Handle invalid favorites gracefully
              const key = favorite?.key || `invalid-${index}`;
              const title = favorite?.title || "Invalid favorite";
              const projectId = favorite?.projectId || "";
              const isValid = favorite?.key && favorite?.title && favorite?.projectId;

              return (
                <div key={key} className={styles.favoriteCard}>
                  <NavigationLink
                    to={isValid ? `/${projectId}/form?key=${key}` : "#"}
                    className={styles.favoriteLink}
                    onClick={(e) => {
                      if (!isValid) {
                        e.preventDefault();
                      }
                    }}
                  >
                    <Text size="medium">{title}</Text>
                  </NavigationLink>
                  <span
                    className={styles.unfavoriteIcon}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleFavorite(favorite);
                    }}
                  >
                    <Star fontSize="small" />
                  </span>
                </div>
              );
            })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
